import { useState, useEffect } from 'react'
import ContactForm from '../components/ContactForm'
import { updatePageSEO } from '../lib/seo'

export default function Contact() {
  const [pageData, setPageData] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    window.scrollTo(0, 0)

    const fetchPage = async () => {
      try {
        const res = await fetch('https://api.dev.auxo.dk/public/by-slug/kontakt')
        if (res.ok) {
          const data = await res.json()
          setPageData(data)
          if (data.meta_title || data.meta_description) {
            updatePageSEO(data.meta_title, data.meta_description)
          }
        }
      } catch (err) {
        console.error('Error fetching contact page:', err)
      } finally {
        setLoading(false)
      }
    }

    fetchPage()
  }, [])

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-white">
        <div className="text-center space-y-4">
          <i className="fa-solid fa-spinner animate-spin text-4xl text-brand-orange"></i>
          <p className="text-slate-500 font-medium">Henter indhold...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white">
      {/* Page Header */}
      <section className="bg-[#111111] text-white py-20 relative overflow-hidden border-b border-[#222222]">
        <div className="absolute inset-0 opacity-15">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1920,quality=50,format=auto/assets/c68264d9-2499-44ef-9e49-6b5cd7a6c3b2/4-53.jpg"
            alt="Kontakt BAW Byg"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative mx-auto max-w-7xl px-6 md:px-12 text-left space-y-4">
          <span className="text-xs font-bold uppercase tracking-[0.3em] text-brand-orange block">KONTAKT OS I DAG</span>
          <h1 className="text-3xl md:text-5xl font-extrabold tracking-tight uppercase">Kontakt BAW Byg</h1>
          <p className="text-slate-300 max-w-2xl text-sm md:text-base leading-relaxed">
            Vi står altid klar til at tage en uforpligtende snak om dine idéer, dit projekt og give et godt tilbud.
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-24 mx-auto max-w-7xl px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-16 items-start bg-white">
        {/* Contact Info Column */}
        <div className="lg:col-span-5 space-y-8">
          <div className="space-y-4">
            <span className="text-xs font-bold uppercase tracking-widest text-brand-orange block">KONTAKTOPLYSNINGER</span>
            <h2 className="text-3xl font-extrabold text-[#111111] tracking-tight uppercase">
              BAW Byg og Konstruktion
            </h2>
            <p className="text-slate-600 text-sm leading-relaxed">
              Drives af Benjamin Petersen, uddannet tømrer og byggetekniker. Vi har stort fokus på at levere førsteklasses håndværk og personlig service.
            </p>
          </div>

          {/* Info cards */}
          <div className="space-y-4">
            <div className="bg-slate-50 p-6 rounded-none border border-slate-200 flex items-start gap-4">
              <div className="bg-[#222222] text-brand-orange h-12 w-12 rounded-none flex items-center justify-center text-lg shrink-0 border border-brand-orange/20">
                <i className="fa-solid fa-phone"></i>
              </div>
              <div className="space-y-1">
                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider block">Telefonnummer</span>
                <a href="tel:+4529678107" className="font-bold text-[#111111] hover:text-brand-orange text-lg transition-colors whitespace-nowrap block">
                  +45 2967 8107
                </a>
                <span className="text-xs text-slate-500 block">Vi svarer altid hurtigt på telefonen.</span>
              </div>
            </div>

            <div className="bg-slate-50 p-6 rounded-none border border-slate-200 flex items-start gap-4">
              <div className="bg-[#222222] text-brand-orange h-12 w-12 rounded-none flex items-center justify-center text-lg shrink-0 border border-brand-orange/20">
                <i className="fa-solid fa-envelope"></i>
              </div>
              <div className="space-y-1">
                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider block">E-mail adresse</span>
                <a href="mailto:kontakt@baw-byg.dk" className="font-bold text-[#111111] hover:text-brand-orange text-lg transition-colors whitespace-nowrap block">
                  kontakt@baw-byg.dk
                </a>
                <span className="text-xs text-slate-500 block">Skriv til os, og vi vender tilbage hurtigt.</span>
              </div>
            </div>

            <div className="bg-slate-50 p-6 rounded-none border border-slate-200 flex items-start gap-4">
              <div className="bg-[#222222] text-brand-orange h-12 w-12 rounded-none flex items-center justify-center text-lg shrink-0 border border-brand-orange/20">
                <i className="fa-solid fa-location-dot"></i>
              </div>
              <div className="space-y-1">
                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider block">Adresse &amp; CVR</span>
                <span className="font-bold text-[#111111] text-base block">
                  Øster Kringelvej 7, 8250 Egå
                </span>
                <span className="text-xs text-slate-500 block">CVR-nummer: 28620136</span>
              </div>
            </div>
          </div>

          {/* Map CTA */}
          <div className="bg-[#222222] text-white p-8 rounded-none border border-[#333333] space-y-4">
            <h3 className="font-display font-bold text-lg text-brand-orange uppercase tracking-wider">
              Find os på kortet
            </h3>
            <p className="text-slate-300 text-xs leading-relaxed">
              Vi holder til i Egå, men dækker hele Aarhus, Risskov, Lystrup, Skødstrup og omegn. Klik herunder for at se vores placering på Google Maps.
            </p>
            <a
              href="https://www.google.com/maps/place/%C3%98ster+Kringelvej+7,+8250+Eg%C3%A5/@56.214598,10.2633334,1304m/data=!3m2!1e3!4b1!4m6!3m5!1s0x464c3e85843f2b79:0x599fb2843f76de85!8m2!3d56.214595!4d10.2659083!16s%2Fg%2F11c1bdf9qq?entry=ttu"
              target="_blank"
              rel="noopener noreferrer"
              className="border border-brand-orange text-brand-orange hover:bg-brand-orange hover:text-white font-bold py-3 px-6 rounded-none transition-all duration-300 text-xs inline-flex items-center gap-2 cursor-pointer uppercase tracking-widest"
            >
              Åben Google Maps
              <i className="fa-solid fa-map-location-dot"></i>
            </a>
          </div>
        </div>

        {/* Form Column */}
        <div className="lg:col-span-7 bg-white p-8 md:p-12 border border-slate-200 shadow-none">
          <ContactForm subject="Henvendelse fra Kontaktsiden" dark={false} />
        </div>
      </section>
    </div>
  )
}
